/**
 * One requestAnimationFrame loop for the entire terminal.
 *
 * This is the single most important performance decision in the app. Each
 * canvas panel could run its own rAF, but then eight panels means eight
 * callbacks the browser schedules independently, eight chances to overrun the
 * frame budget, and no way to prioritise. With one loop we get:
 *
 *   - a real frame budget we can measure and enforce
 *   - deterministic draw order (chart before overlays before crosshair)
 *   - the ability to skip a renderer that reports itself clean
 *   - a single place to pause everything when the tab is hidden
 *
 * Renderers register a `draw` and, optionally, a `dirty` predicate. A renderer
 * whose `dirty()` returns false costs one function call per frame instead of a
 * canvas repaint.
 */

import { Rolling } from './ring';

export interface FrameTask {
  /** Lower runs first. Chart = 0, overlays = 10, hud/crosshair = 20. */
  priority: number;
  /**
   * Return false to skip the draw this frame. Omit to always draw.
   * Must be cheap — it runs every frame for every registered task.
   */
  dirty?: () => boolean;
  draw: (now: number, dt: number) => void;
}

export interface FrameStats {
  fps: number;
  /** Mean ms spent inside our draw calls (not total frame time). */
  drawMs: number;
  /** 95th percentile frame interval, the number that actually shows jank. */
  p95: number;
  frames: number;
  tasks: number;
}

class FrameLoop {
  private tasks: FrameTask[] = [];
  private running = false;
  private handle = 0;
  private last = 0;

  private frameTimes = new Rolling(120);
  private drawTimes = new Rolling(120);
  private frameCount = 0;
  private fpsWindowStart = 0;
  private fpsWindowFrames = 0;
  private currentFps = 0;

  /** Set while the tab is hidden; we stop drawing but keep ingesting data. */
  private hidden = false;

  constructor() {
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        this.hidden = document.hidden;
        if (!this.hidden) {
          // Coming back: reset timing so one huge dt does not poison stats.
          this.last = performance.now();
          this.frameTimes.clear();
          this.drawTimes.clear();
        }
      });
    }
  }

  add(task: FrameTask): () => void {
    this.tasks.push(task);
    this.tasks.sort((a, b) => a.priority - b.priority);
    this.start();
    return () => this.remove(task);
  }

  remove(task: FrameTask): void {
    const i = this.tasks.indexOf(task);
    if (i >= 0) this.tasks.splice(i, 1);
    if (this.tasks.length === 0) this.stop();
  }

  private start(): void {
    if (this.running) return;
    this.running = true;
    this.last = performance.now();
    this.fpsWindowStart = this.last;
    this.fpsWindowFrames = 0;
    this.handle = requestAnimationFrame(this.tick);
  }

  private stop(): void {
    if (!this.running) return;
    this.running = false;
    cancelAnimationFrame(this.handle);
  }

  private tick = (now: number): void => {
    this.handle = requestAnimationFrame(this.tick);

    const dt = now - this.last;
    this.last = now;

    // A hidden tab still gets rAF at ~1Hz in some browsers; do not draw.
    if (this.hidden) return;

    // Guard against the enormous dt after a tab restore or a long GC pause.
    if (dt > 0 && dt < 1000) this.frameTimes.push(dt);

    this.frameCount++;
    this.fpsWindowFrames++;
    if (now - this.fpsWindowStart >= 500) {
      this.currentFps = (this.fpsWindowFrames * 1000) / (now - this.fpsWindowStart);
      this.fpsWindowStart = now;
      this.fpsWindowFrames = 0;
    }

    const t0 = performance.now();
    const tasks = this.tasks;
    for (let i = 0; i < tasks.length; i++) {
      const t = tasks[i];
      if (t.dirty && !t.dirty()) continue;
      try {
        t.draw(now, dt);
      } catch (err) {
        console.error('[frame] task threw, removing it', err);
        this.remove(t);
        i--;
      }
    }
    this.drawTimes.push(performance.now() - t0);
  };

  stats(): FrameStats {
    return {
      fps: this.currentFps,
      drawMs: this.drawTimes.mean(),
      p95: this.frameTimes.percentile(0.95),
      frames: this.frameCount,
      tasks: this.tasks.length,
    };
  }
}

export const frameLoop = new FrameLoop();

/**
 * Size a canvas to its CSS box at device pixel ratio and return the 2D
 * context ready to draw in CSS pixels.
 *
 * Returns null when the element has no layout yet (display:none, or the first
 * frame before the flex tree settles), which callers must treat as "skip".
 */
export interface CanvasSurface {
  ctx: CanvasRenderingContext2D;
  width: number;
  height: number;
  dpr: number;
}

export function acquireSurface(
  canvas: HTMLCanvasElement,
  cssWidth: number,
  cssHeight: number,
): CanvasSurface | null {
  if (cssWidth <= 0 || cssHeight <= 0) return null;
  // Cap DPR: a 3x retina phone rendering a 2000px-wide chart is 6000 device
  // pixels of fill per frame for no visible gain.
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const w = Math.max(1, Math.round(cssWidth * dpr));
  const h = Math.max(1, Math.round(cssHeight * dpr));
  if (canvas.width !== w) canvas.width = w;
  if (canvas.height !== h) canvas.height = h;

  const ctx = canvas.getContext('2d', { alpha: true, desynchronized: true });
  if (!ctx) return null;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  return { ctx, width: cssWidth, height: cssHeight, dpr };
}

/** Crisp 1px line: align to the half-pixel grid. */
export function crisp(v: number): number {
  return Math.round(v) + 0.5;
}
